import { useState, useEffect, useRef } from "react";
import React from 'react';
import { Activity, Badge, ChevronUp, Loader2, MessageSquare, Save } from "lucide-react";
import { ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../Dashboard/cards";
import { updateCustomerRiskApi, getRiskAnalysisListApi } from "../../../services/axios/AuthApis/RisksManagementApis";
import { IMPACT_OPTIONS, LIKELIHOOD_OPTIONS } from "../../../constants/AppEnums";
import { DataTable } from "../../common/DataTable";
import { useSelector } from "react-redux";

// Fully independent of Risk Register - its own state, its own fetch
// function, its own backend endpoint (getRiskAnalysisListApi /
// get_risk_analysis_list), per explicit request to isolate Risk
// Analysis's filter wiring instead of sharing RiskManagement/index.js's
// textColumnFilters/getRiskList.
export const RiskAnalysis = () => {
  const userDetail = useSelector((state) => state.user);
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const userRole = userDetail?.user?.role || '';
  const disable = userRole == "auditor";
  const customerId = (userRole === 'security_analyst' || userRole === 'auditor')
    ? selectedCompany?.user_id
    : userDetail?.user?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [remarks, setRemarks] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [risks, setRisks] = useState([]);
  const [totalRiskCount, setTotalRiskCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [textColumnFilters, setTextColumnFilters] = useState({});
  const requestIdRef = useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalRiskCount / itemsPerPage));
  const columnFilters = Object.entries(textColumnFilters || {})
    .filter(([, value]) => value)
    .map(([id, value]) => ({ id, value }));

  const fetchRisks = async (page = currentPage) => {
    if (!customerId) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = await getRiskAnalysisListApi(customerId, page, itemsPerPage, columnFilters);
      if (requestId !== requestIdRef.current) return;
      if (response.success) {
        setRisks(response.data || []);
        setTotalRiskCount(response.total_count || 0);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Failed to load risk analysis list", err);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchRisks(1);
  }, [customerId, itemsPerPage, JSON.stringify(columnFilters)]);

  const handlePageChange = (page, pageSize) => {
    if (pageSize !== undefined && pageSize !== itemsPerPage) {
      setItemsPerPage(pageSize);
      setCurrentPage(1);
      return;
    }
    if (page >= 1 && page <= totalPages) {
      fetchRisks(page);
    }
  };
  const getScoreFromValue = (options, value) => {
    return options.find(opt => opt.value === value)?.score || 0;
  }
  const calculateRating = (impact, likelihood) => {
    if (!impact || !likelihood) return "";
    const impactScore = getScoreFromValue(IMPACT_OPTIONS, impact);
    const likelihoodScore = getScoreFromValue(LIKELIHOOD_OPTIONS, likelihood);
    const total = impactScore + likelihoodScore;

    if (total >= 9) return "Critical (9-10)";
    if (total >= 7) return "High (8)";
    if (total >= 6) return "Significant (6-7)";
    if (total >= 4) return "Moderate (4-5)";
    return "Low (2-3)";
  };

  const updateRisk = async (riskId, field, value) => {
    const risk = risks.find(r => r.id === riskId);
    if (!risk) return;
    setSaving(prev => ({ ...prev, [riskId]: true }));
    const newImpact = field === "inherent_impact_type" ? value : risk.inherent_impact_type;
    const newLikelihood = field === "inherent_likelihood_type" ? value : risk.inherent_likelihood_type;
    const newRating = calculateRating(newImpact, newLikelihood);

    try {
      const updateData = {
        ...risk,
        inherent_impact_type: newImpact,
        inherent_likelihood_type: newLikelihood,
        inherent_risk_score: newRating
      };
      const response = await updateCustomerRiskApi(updateData, risk.id);
      if (response.success) {
        setRisks(updateData => risks.map(r => r.id === riskId ? { ...r, ...updateData } : r));
        fetchRisks(currentPage);
      }
    } catch (err) {
      console.log(err);
      console.error("Failed to update risk ".concat(err));
    } finally {
      setSaving(prev => ({ ...prev, [riskId]: false }));
    }
  };

  const saveRemarks = async (riskId) => {
    const risk = risks.find(r => r.id === riskId);
    if (!risk) return;
    setSaving(prev => ({ ...prev, [`remarks_${riskId}`]: true }));
    try {
      const updateData = {
        ...risk,
        reason_for_risk_response: remarks[riskId] || ""
      };

      const response = await updateCustomerRiskApi(updateData, risk.id);
      if (response.success) {
        setRisks(updateData => risks.map(r => r.id === riskId ? { ...r, ...updateData } : r));
        fetchRisks(currentPage);
      }
    } catch (err) {
      console.error("Failed to update risk");
    } finally {
      setSaving(prev => ({ ...prev, [riskId]: false }));
    }
  };
  const getRatingClass = (rating) => {
    if (!rating) return "bg-gray-100 text-gray-700";
    if (rating.includes("critical")) return "bg-red-500 text-white";
    if (rating.includes("high")) return "bg-orange-500 text-white";
    if (rating.includes("significant")) return "bg-amber-500 text-white";
    if (rating.includes("moderate")) return "bg-yellow-400 text-yellow-900";
    return "bg-green-500 text-white";
  };

  // Remarks is rendered inline inside this column's own cell (not as a
  // connected full-width sub-row) - DataTableBody renders exactly one
  // <tr> per row with no support for an expandable extra row, so the
  // expand/collapse panel has to live inside the cell itself.
  const columns = [
    {
      accessorKey: "risk_id",
      header: "Risk ID",
      size: 128,
      meta: { filter: { variant: "text", label: "Risk ID", placeholder: "Filter risk ID" } },
      cell: ({ row }) => (
        <span className="font-mono text-blue-600 font-medium">{row.original.risk_id}</span>
      ),
    },
    {
      accessorKey: "risk_description",
      header: "Description",
      size: 300,
      meta: { filter: { variant: "text", label: "Description", placeholder: "Filter description" } },
      cell: ({ row }) => (
        <span className="text-slate-700">{row.original.risk_description}</span>
      ),
    },
    {
      id: "impact",
      accessorFn: (risk) => risk.inherent_impact_type,
      header: "Impact",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Impact", options: IMPACT_OPTIONS } },
      cell: ({ row }) => {
        const risk = row.original;
        return (
          <Select
            value={risk.inherent_impact_type || "none"}
            onValueChange={(val) => updateRisk(risk.id, "inherent_impact_type", val === "none" ? "" : val)}
            disabled={saving[risk.id] || disable}
          >
            <SelectTrigger className="w-[140px] bg-white border-gray-300">
              <SelectValue placeholder="Select impact" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {IMPACT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      id: "likelihood",
      accessorFn: (risk) => risk.inherent_likelihood_type,
      header: "Likelihood",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Likelihood", options: LIKELIHOOD_OPTIONS } },
      cell: ({ row }) => {
        const risk = row.original;
        return (
          <Select
            value={risk.inherent_likelihood_type || "none"}
            onValueChange={(val) => updateRisk(risk.id, "inherent_likelihood_type", val === "none" ? "" : val)}
            disabled={saving[risk.id] || disable}
          >
            <SelectTrigger className="w-[140px] bg-white border-gray-300">
              <SelectValue placeholder="Select likelihood" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200">
              {LIKELIHOOD_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: "inherent_risk_score",
      header: "Risk Rating",
      enableSorting: false,
      cell: ({ row }) => (
        <span className={`px-3 py-1.5 rounded text-xs font-semibold ${getRatingClass(row.original.inherent_risk_score)}`}>
          {row.original.inherent_risk_score || "Not Assessed"}
        </span>
      ),
    },
    {
      id: "remarks",
      header: "Remarks",
      enableSorting: false,
      size: 320,
      cell: ({ row }) => {
        const risk = row.original;
        const isOpen = expandedRow === risk.id;
        return (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpandedRow(isOpen ? null : risk.id)}
              className="text-slate-600 hover:text-white hover:bg-white bg-blue-600 text-white"
            >
              {isOpen ? <ChevronUp className="w-4 h-4 mr-1" /> : <MessageSquare className="w-4 h-4 mr-1" />}
              {isOpen ? "Hide" : "Add/View"}
            </Button>

            {isOpen && (
              <div className="mt-3 space-y-3 w-80 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">
                    Analysis Remarks / Notes
                    <span className="text-xs text-gray-500 ml-2 font-normal">(For risk: {risk.risk_id})</span>
                  </label>
                  <Badge variant="outline" className="text-xs">{risk.risk_type || "No type"}</Badge>
                </div>
                <Textarea
                  value={remarks[risk.id] ?? risk.reason_for_risk_response ?? ""}
                  onChange={(e) => setRemarks(prev => ({ ...prev, [risk.id]: e.target.value }))}
                  placeholder="Add analysis notes, justifications, observations, or mitigation suggestions..."
                  className="bg-white border-gray-300 min-h-[100px] resize-y"
                  data-testid={`analysis-remarks-${risk.risk_id}`}
                  disabled={disable}
                />
                {userRole !== "auditor" && (
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={() => setExpandedRow(null)} className="border-gray-300">
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => saveRemarks(risk.id)}
                      disabled={saving[`remarks_${risk.id}`]}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {saving[`remarks_${risk.id}`] ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" />Save Remarks</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },
  ];
  return (
    <div className="space-y-6" data-testid="risk-analysis-tab">
      {/* 5x5 Matrix Reference */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center">
            <Activity className="w-4 h-4 text-blue-500" />
            5x5 Risk Matrix Reference
          </CardTitle>
        </CardHeader>
        <CardContent key="risk-matrix-1">
          <div className="grid grid-cols-6 gap-0.5 max-w-2xl">
            <div></div>
            <div className="text-center text-xs text-slate-500 font-medium p-2">Rare</div>
            <div className="text-center text-xs text-slate-500 font-medium p-2">Unlikely</div>
            <div className="text-center text-xs text-slate-500 font-medium p-2">Possible</div>
            <div className="text-center text-xs text-slate-500 font-medium p-2">Likely</div>
            <div className="text-center text-xs text-slate-500 font-medium p-2">Almost Certain</div>

            {["Catastrophic", "Major", "Moderate", "Minor", "Insignificant"].map((impact, impactIdx) => {
              const impactScore = 5 - impactIdx;
              return (
                <React.Fragment key={`row-${impact}`}>
                  <div className="text-xs text-slate-500 font-medium p-2 flex items-center">{impact}</div>
                  {[1, 2, 3, 4, 5].map((likelihood) => {
                    const score = impactScore + likelihood;
                    let cellClass = "bg-green-200";
                    if (score >= 9) cellClass = "bg-red-500 text-white";
                    else if (score >= 7) cellClass = "bg-orange-400 text-white";
                    else if (score >= 5) cellClass = "bg-amber-400";
                    else if (score >= 3) cellClass = "bg-yellow-200";

                    return (
                      <div
                        key={`${impactScore}-${likelihood}`}
                        className={`h-10 rounded flex items-center justify-center text-xs font-semibold ${cellClass}`}
                      >
                        {score}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="card-surface">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-800 text-left">
            Inherent Risk Assessment
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={risks}
            getRowId={(risk) => String(risk.id)}
            loading={loading}
            maxBodyHeight="65vh"
            emptyMessage={(
              <div className="flex flex-col items-center justify-center text-gray-400">
                <ShieldExclamationIcon className="mb-3 h-14 w-14 opacity-40" />
                <p className="text-lg font-medium">No risks found</p>
                <p className="mt-1 text-sm">Try adjusting your search or filters</p>
              </div>
            )}
            manualPagination
            rowCount={totalRiskCount}
            pagination={{ pageIndex: currentPage - 1, pageSize: itemsPerPage }}
            onPaginationChange={(updater) => {
              const currentPagination = { pageIndex: currentPage - 1, pageSize: itemsPerPage };
              const nextPagination = typeof updater === 'function'
                ? updater(currentPagination)
                : updater;
              handlePageChange(nextPagination.pageIndex + 1, nextPagination.pageSize);
            }}
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            manualFiltering
            columnFilters={columnFilters}
            onColumnFiltersChange={(updater) => {
              const nextFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
              const values = Object.fromEntries(nextFilters.map(({ id, value }) => [id, value]));
              // Same whitelist as Risk Register's onColumnFiltersChange -
              // only forward column ids the backend's _RISK_TEXT_FILTER_FIELDS
              // actually accepts, so a stray id can never 400 the request.
              const textFilters = Object.fromEntries(
                ['risk_id', 'risk_description', 'impact', 'likelihood']
                  .filter((id) => values[id])
                  .map((id) => [id, values[id]]),
              );
              setTextColumnFilters?.(textFilters);
              setCurrentPage(1);
            }}
            className="overflow-hidden rounded-xl shadow-2xl"
          />
        </CardContent>
      </Card>
      {/*Ends here */}
    </div >
  );
};

